// src/utils/auth.js

const PENDING_SUBSCRIPTION_KEY = 'mp_pending_subscription';

export const AUTH_CHANGED_EVENT = 'mp-auth-changed';

const STORAGE_KEYS = {
    TOKEN: 'mp_token',
    USER: 'mp_user',
    TOKEN_EXP: 'mp_token_exp',
    TOKEN_TTL: 'mp_token_ttl',
    LOGIN_AT: 'mp_login_at',
};

const isBrowser = () => typeof window !== 'undefined' && typeof localStorage !== 'undefined';

const dispatchAuthChanged = () => {
    if (!isBrowser()) return;

    window.dispatchEvent(
        new CustomEvent(AUTH_CHANGED_EVENT, {
            detail: {
                timestamp: Date.now(),
            },
        })
    );
};

export const parseJwt = (token) => {
    try {
        if (!token || typeof token !== 'string') {
            return null;
        }

        const parts = token.split('.');

        if (parts.length !== 3) {
            return null;
        }

        const base64Url = parts[1];

        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
};

export const getJwtExpiryMs = (token) => {
    const payload = parseJwt(token);

    if (!payload?.exp) {
        return null;
    }

    return Number(payload.exp) * 1000;
};

export const isJwtExpired = (token) => {
    const expiryMs = getJwtExpiryMs(token);

    if (!expiryMs) {
        return true;
    }

    return Date.now() >= expiryMs;
};

export const getStoredAuthExpiryMs = () => {
    if (!isBrowser()) {
        return null;
    }

    const storedExp = localStorage.getItem(STORAGE_KEYS.TOKEN_EXP);

    if (storedExp) {
        const expMs = Number(storedExp);

        if (Number.isFinite(expMs) && expMs > 0) {
            return expMs;
        }
    }

    const loginAt = Number(localStorage.getItem(STORAGE_KEYS.LOGIN_AT));

    const ttlSeconds = Number(localStorage.getItem(STORAGE_KEYS.TOKEN_TTL));

    if (Number.isFinite(loginAt) && Number.isFinite(ttlSeconds) && loginAt > 0 && ttlSeconds > 0) {
        return loginAt + ttlSeconds * 1000;
    }

    return null;
};

export const getAuthToken = () => {
    if (!isBrowser()) {
        return null;
    }

    return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

export const getAuthUser = () => {
    try {
        if (!isBrowser()) {
            return null;
        }

        const raw = localStorage.getItem(STORAGE_KEYS.USER);

        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const isStoredAuthExpired = () => {
    const token = getAuthToken();
    const user = getAuthUser();

    if (!token || !user) {
        return true;
    }

    const expiryMs = getStoredAuthExpiryMs();

    if (!expiryMs) {
        return false;
    }

    return Date.now() >= expiryMs;
};

export const isAuthenticated = () => {
    const token = getAuthToken();
    const user = getAuthUser();

    return Boolean(token) && Boolean(user) && !isStoredAuthExpired();
};

export const saveAuth = ({ token, user, expiresInSeconds }) => {
    if (!isBrowser()) {
        return;
    }

    if (!token || !user) {
        throw new Error('Token dan user wajib tersedia.');
    }

    const now = Date.now();

    localStorage.setItem(STORAGE_KEYS.TOKEN, token);

    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

    localStorage.setItem(STORAGE_KEYS.LOGIN_AT, String(now));

    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXP);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_TTL);

    const jwtExpiryMs = getJwtExpiryMs(token);

    if (jwtExpiryMs) {
        localStorage.setItem(STORAGE_KEYS.TOKEN_EXP, String(jwtExpiryMs));
    } else if (Number.isFinite(expiresInSeconds) && expiresInSeconds > 0) {
        localStorage.setItem(STORAGE_KEYS.TOKEN_TTL, String(expiresInSeconds));

        localStorage.setItem(STORAGE_KEYS.TOKEN_EXP, String(now + expiresInSeconds * 1000));
    }

    dispatchAuthChanged();
};

export const clearAuth = () => {
    if (!isBrowser()) {
        return;
    }

    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXP);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_TTL);
    localStorage.removeItem(STORAGE_KEYS.LOGIN_AT);

    sessionStorage.removeItem(PENDING_SUBSCRIPTION_KEY);

    dispatchAuthChanged();
};

export const getAuthSnapshot = () => ({
    token: getAuthToken(),
    user: getAuthUser(),
    authenticated: isAuthenticated(),
    expired: isStoredAuthExpired(),
    expiresAt: getStoredAuthExpiryMs(),
});

export const savePendingSubscriptionSelection = (selection) => {
    if (!isBrowser()) {
        return;
    }

    sessionStorage.setItem(PENDING_SUBSCRIPTION_KEY, JSON.stringify(selection));
};

export const getPendingSubscriptionSelection = () => {
    try {
        if (!isBrowser()) {
            return null;
        }

        const raw = sessionStorage.getItem(PENDING_SUBSCRIPTION_KEY);

        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const clearPendingSubscriptionSelection = () => {
    if (!isBrowser()) {
        return;
    }

    sessionStorage.removeItem(PENDING_SUBSCRIPTION_KEY);
};
